import { apiClient, unwrap, unwrapPaginated } from './client';
import type {
  AffectationOf,
  AlertesResult,
  BonCommandeAchat,
  CategorieFournisseur,
  DashboardDg,
  RoleAffectationOf,
  Chantier,
  ClasseBeton,
  Commande,
  ConformiteNFResult,
  DashboardQC,
  DNC,
  Eprouvette,
  EquipeProduction,
  Expedition,
  CatalogueGroupe,
  FamilleProduit,
  FormuleBeton,
  Fournisseur,
  Gachee,
  InventaireParc,
  LotProduction,
  LotProductionDetail,
  MaintenanceMoule,
  MatierePremiere,
  Moule,
  MouvementParc,
  MouvementStock,
  OrdreFabrication,
  OrdreFabricationDetail,
  Planning,
  PrixApplicable,
  Produit,
  ProduitWithBom,
  ReceptionAchat,
  ReclamationChantier,
  StatutBonCommande,
  StatutChantier,
  StatutCommande,
  StatutDNC,
  StatutEprouvette,
  StatutExpedition,
  StatutGachee,
  StatutLot,
  StatutMaintenanceMoule,
  StatutMoule,
  StatutOF,
  StatutReclamation,
  StatutTarif,
  StatutToupie,
  StockHealth,
  TarifContratCadre,
  Toupie,
  TypeMaintenanceMoule,
  TypeMatierePremiere,
  TypeMouvementParc,
  TypeMouvementStock,
  TypeTransportExpedition,
} from '@/types';
import type { Invoice } from '@/types';

// ── PAGINATED RESPONSES ─────────────────────────────────────────────────────

export interface Paginated<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; pages: number };
}

// ── PRODUITS ────────────────────────────────────────────────────────────────

export interface ProduitListParams {
  famille?: FamilleProduit;
  /** Identifiant du groupe, ou « aucun » pour les non classés. */
  groupeId?: string;
  search?:  string;
  actif?:   boolean;
  page?:    number;
  limit?:   number;
}

export interface CreateProduitPayload {
  code:                string;
  famille:             FamilleProduit;
  designation:         string;
  prixVenteCents:      number;
  sousFamille?:        string;
  /** null = retirer du groupe (« Non classés »). */
  groupeId?:           string | null;
  longueurM?:          number;
  largeurMm?:          number;
  hauteurMm?:          number;
  epaisseurMm?:        number;
  poidsKg?:            number;
  volumeBetonM3?:      number;
  resistanceFc28?:     number;
  norme?:              string;
  classeProduit?:      string;
  classeBeton?:        ClasseBeton;
  effortNominalDaN?:   number;
  chargeRuptureDaN?:   number;
  // Préfa Phase 1
  formuleBetonId?:        string;
  ferraillageKgParPiece?: number;
  delaiCureJours?:        number;
  mouleIdDefault?:        string;
  photoUrl?:              string;
  ficheTechniquePdfUrl?:  string;
  // Commerce
  uniteVente?:         string;
  machineFabrication?: string;
  tempsCycleMin?:      number;
  stockActuel?:        number;
  seuilAlerte?:        number;
  actif?:              boolean;
}

export type UpdateProduitPayload = Partial<CreateProduitPayload>;

export interface BomItemPayload {
  matierePremiereId: string;
  quantite:          number;
  notes?:            string;
}

// ── MATIÈRES PREMIÈRES ──────────────────────────────────────────────────────

export interface MatiereListParams {
  search?: string;
  actif?:  boolean;
  page?:   number;
  limit?:  number;
}

export interface CreateMatierePayload {
  code:              string;
  nom:               string;
  unite:             string;
  prixUnitaireCents: number;
  description?:      string;
  fournisseurNom?:   string;
  stockActuel?:      number;
  seuilAlerte?:      number;
  actif?:            boolean;
}

export type UpdateMatierePayload = Partial<CreateMatierePayload>;

// ── API CLIENT ──────────────────────────────────────────────────────────────

// ── PRODUCTION JOURNALIÈRE (préfabriqué) ────────────────────────────────────

export interface ConsommationUnitaireMp {
  matiereId:   string;
  code:        string;
  nom:         string;
  unite:       string;
  parUnite:    number;
  stockActuel: number;
}

export interface ProductionJournaliereOf {
  ordreId:              string;
  reference:            string;
  statut:               string;
  datePlanifiee:        string;
  equipe:               { id: string; nom: string } | null;
  produit:              { id: string; code: string; designation: string; famille: string; uniteVente: string };
  quantitePlanifiee:    number;
  dejaProduit:          number;
  resteAProduire:       number;
  consommationUnitaire: ConsommationUnitaireMp[];
  ferraillageKgParPiece: number | null;
}

export interface ProductionJournaliereLotQc {
  id:             string;
  lotNumber:      string;
  quantite:       number;
  dateProduction: string;
  notes:          string | null;
  ordre: {
    id: string; reference: string;
    produit: { id: string; code: string; designation: string; norme: string | null; classeProduit: string | null; uniteVente: string };
  };
}

/** Produit du catalogue, pour la production destinée au stock (sans OF). */
export interface ProduitPourStock {
  id:                    string;
  code:                  string;
  designation:           string;
  famille:               string;
  uniteVente:            string;
  consommationUnitaire:  ConsommationUnitaireMp[];
  ferraillageKgParPiece: number | null;
}

export interface DeclarationDuJour {
  id:             string;
  lotNumber:      string;
  quantite:       number;
  statut:         string;
  ordreReference: string;
  produit:        { code: string; designation: string };
  pourStock:      boolean;
}

export interface ProductionJournaliereResponse {
  date:               string;
  produits:           ProduitPourStock[];
  ordres:             ProductionJournaliereOf[];
  declarationsDuJour: DeclarationDuJour[];
  fileQc:             ProductionJournaliereLotQc[];
  totaux:             { ordresOuverts: number; produitCeJour: number; lotsEnAttenteQc: number };
}

export interface DeclarerProductionPayload {
  date?:  string;
  /** Chaque ligne cible soit un OF existant (sur commande), soit un produit (pour stock). */
  lignes: Array<{ ordreId?: string; produitId?: string; quantiteProduite: number; notes?: string }>;
}

export interface AlerteStockProduction {
  matiereId:   string;
  code:        string;
  nom:         string;
  unite:       string;
  stockApres:  number;
  seuilAlerte: number;
  severite:    'danger' | 'warning';
  message:     string;
}

export interface DeclarerProductionResponse {
  date:         string;
  lotsCrees:    Array<{ id: string; lotNumber: string; ordreRef: string; produitCode: string; quantite: number; pourStock: boolean }>;
  ofClotures:   string[];
  ofStockCrees: string[];
  alertesStock: AlerteStockProduction[];
  message:      string;
}

export interface ValiderQcParcPayload {
  lotIds:           string[];
  zoneParc:         string;
  emplacementParc?: string;
  fc28Mesure?:      number;
  motif?:           string;
}

export interface RejeterQcPayload {
  lotIds: string[];
  motif:  string;
}

export interface GroupesCatalogue {
  groupes:      Array<CatalogueGroupe & { nbProduits: number }>;
  nbNonClasses: number;
}

export const operationsApi = {
  beton: {
    // Groupes de catalogue (24/10/2026)
    listGroupes: async (): Promise<GroupesCatalogue> => unwrap<GroupesCatalogue>(await apiClient.get('/operations/beton/catalogue-groupes')),
    creerGroupe: async (libelle: string) => unwrap<CatalogueGroupe>(await apiClient.post('/operations/beton/catalogue-groupes', { libelle })),
    modifierGroupe: async (id: string, payload: { libelle?: string; actif?: boolean }) =>
      unwrap<CatalogueGroupe>(await apiClient.patch(`/operations/beton/catalogue-groupes/${id}`, payload)),
    ordonnerGroupes: async (ids: string[]) => unwrap<GroupesCatalogue>(await apiClient.put('/operations/beton/catalogue-groupes/ordre', { ids })),

    // Produits
    listProduits: async (params: ProduitListParams = {}): Promise<Paginated<Produit>> => {
      const res = await apiClient.get('/operations/beton/produits', { params });
      return unwrapPaginated<Produit>(res);
    },

    getProduit: async (id: string): Promise<ProduitWithBom> => {
      const res = await apiClient.get(`/operations/beton/produits/${id}`);
      return unwrap<ProduitWithBom>(res);
    },

    createProduit: async (payload: CreateProduitPayload): Promise<Produit> => {
      const res = await apiClient.post('/operations/beton/produits', payload);
      return unwrap<Produit>(res);
    },

    updateProduit: async (id: string, payload: UpdateProduitPayload): Promise<Produit> => {
      const res = await apiClient.patch(`/operations/beton/produits/${id}`, payload);
      return unwrap<Produit>(res);
    },

    deleteProduit: async (id: string): Promise<Produit> => {
      const res = await apiClient.delete(`/operations/beton/produits/${id}`);
      return unwrap<Produit>(res);
    },

    replaceBom: async (id: string, items: BomItemPayload[]): Promise<ProduitWithBom> => {
      const res = await apiClient.put(`/operations/beton/produits/${id}/bom`, { items });
      return unwrap<ProduitWithBom>(res);
    },

    // Matières premières
    listMatieres: async (params: MatiereListParams = {}): Promise<Paginated<MatierePremiere>> => {
      const res = await apiClient.get('/operations/beton/matieres-premieres', { params });
      return unwrapPaginated<MatierePremiere>(res);
    },

    getMatiere: async (id: string): Promise<MatierePremiere> => {
      const res = await apiClient.get(`/operations/beton/matieres-premieres/${id}`);
      return unwrap<MatierePremiere>(res);
    },

    createMatiere: async (payload: CreateMatierePayload): Promise<MatierePremiere> => {
      const res = await apiClient.post('/operations/beton/matieres-premieres', payload);
      return unwrap<MatierePremiere>(res);
    },

    updateMatiere: async (id: string, payload: UpdateMatierePayload): Promise<MatierePremiere> => {
      const res = await apiClient.patch(`/operations/beton/matieres-premieres/${id}`, payload);
      return unwrap<MatierePremiere>(res);
    },

    deleteMatiere: async (id: string): Promise<MatierePremiere | { deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/matieres-premieres/${id}`);
      return unwrap<MatierePremiere | { deleted: true }>(res);
    },

    // ── Ordres de Fabrication (Sprint LB-B) ─────────────────
    listOrdres: async (params: OrdreListParams = {}): Promise<Paginated<OrdreFabrication>> => {
      const res = await apiClient.get('/operations/beton/ordres', { params });
      return unwrapPaginated<OrdreFabrication>(res);
    },

    getOrdre: async (id: string): Promise<OrdreFabricationDetail> => {
      const res = await apiClient.get(`/operations/beton/ordres/${id}`);
      return unwrap<OrdreFabricationDetail>(res);
    },

    createOrdre: async (payload: CreateOrdrePayload): Promise<OrdreFabrication> => {
      const res = await apiClient.post('/operations/beton/ordres', payload);
      return unwrap<OrdreFabrication>(res);
    },

    startOrdre: async (id: string): Promise<OrdreFabrication> => {
      const res = await apiClient.post(`/operations/beton/ordres/${id}/start`);
      return unwrap<OrdreFabrication>(res);
    },

    completeOrdre: async (id: string, quantiteReelle?: number): Promise<OrdreFabrication> => {
      const res = await apiClient.post(`/operations/beton/ordres/${id}/complete`, { quantiteReelle });
      return unwrap<OrdreFabrication>(res);
    },

    cancelOrdre: async (id: string): Promise<OrdreFabrication> => {
      const res = await apiClient.post(`/operations/beton/ordres/${id}/cancel`);
      return unwrap<OrdreFabrication>(res);
    },

    updateOrdre: async (id: string, payload: UpdateOrdrePayload): Promise<OrdreFabrication> => {
      const res = await apiClient.patch(`/operations/beton/ordres/${id}`, payload);
      return unwrap<OrdreFabrication>(res);
    },

    // ── Tâches préfa (Phase 1.3 — ferraillage/coffrage/coulage/decoffrage) ──
    markOrdreTask: async (id: string, task: OrdreTaskName, payload: OrdreTaskPayload = {}): Promise<OrdreFabrication> => {
      const res = await apiClient.post(`/operations/beton/ordres/${id}/tasks/${task}`, payload);
      return unwrap<OrdreFabrication>(res);
    },

    // ── Planning agrégé N jours ────────────────────────────
    getPlanning: async (params: PlanningParams = {}): Promise<Planning> => {
      const res = await apiClient.get('/operations/beton/planning', { params });
      return unwrap<Planning>(res);
    },

    // ── Estimation production (LB-B v2.2) ───────────────────
    getEstimation: async (produitId: string, quantite: number): Promise<EstimationProduction> => {
      const res = await apiClient.get('/operations/beton/estimation', { params: { produitId, quantite } });
      return unwrap<EstimationProduction>(res);
    },

    // ── Chantiers ──────────────────────────────────────────
    listChantiers: async (params: ChantierListParams = {}): Promise<Paginated<Chantier>> => {
      const res = await apiClient.get('/operations/beton/chantiers', { params });
      return unwrapPaginated<Chantier>(res);
    },

    getChantier: async (id: string): Promise<Chantier> => {
      const res = await apiClient.get(`/operations/beton/chantiers/${id}`);
      return unwrap<Chantier>(res);
    },

    createChantier: async (payload: CreateChantierPayload): Promise<Chantier> => {
      const res = await apiClient.post('/operations/beton/chantiers', payload);
      return unwrap<Chantier>(res);
    },

    updateChantier: async (id: string, payload: UpdateChantierPayload): Promise<Chantier> => {
      const res = await apiClient.patch(`/operations/beton/chantiers/${id}`, payload);
      return unwrap<Chantier>(res);
    },

    deleteChantier: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/chantiers/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Toupies (Flotte BPE) ───────────────────────────────
    listToupies: async (params: ToupieListParams = {}): Promise<Paginated<Toupie>> => {
      const res = await apiClient.get('/operations/beton/toupies', { params });
      return unwrapPaginated<Toupie>(res);
    },

    getToupie: async (id: string): Promise<Toupie> => {
      const res = await apiClient.get(`/operations/beton/toupies/${id}`);
      return unwrap<Toupie>(res);
    },

    createToupie: async (payload: CreateToupiePayload): Promise<Toupie> => {
      const res = await apiClient.post('/operations/beton/toupies', payload);
      return unwrap<Toupie>(res);
    },

    updateToupie: async (id: string, payload: UpdateToupiePayload): Promise<Toupie> => {
      const res = await apiClient.patch(`/operations/beton/toupies/${id}`, payload);
      return unwrap<Toupie>(res);
    },

    deleteToupie: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/toupies/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Lots de production (LB-B v2) ───────────────────────
    listLotsByOrdre: async (ordreId: string, params: LotListParams = {}): Promise<Paginated<LotProduction>> => {
      const res = await apiClient.get(`/operations/beton/ordres/${ordreId}/lots`, { params });
      return unwrapPaginated<LotProduction>(res);
    },

    listAllLots: async (params: LotListParams = {}): Promise<Paginated<LotProductionWithOrdre>> => {
      const res = await apiClient.get('/operations/beton/lots', { params });
      return unwrapPaginated<LotProductionWithOrdre>(res);
    },

    getLot: async (id: string): Promise<LotProductionDetail> => {
      const res = await apiClient.get(`/operations/beton/lots/${id}`);
      return unwrap<LotProductionDetail>(res);
    },

    createLot: async (ordreId: string, payload: CreateLotPayload): Promise<LotProduction> => {
      const res = await apiClient.post(`/operations/beton/ordres/${ordreId}/lots`, payload);
      return unwrap<LotProduction>(res);
    },

    updateLot: async (id: string, payload: UpdateLotPayload): Promise<LotProduction> => {
      const res = await apiClient.patch(`/operations/beton/lots/${id}`, payload);
      return unwrap<LotProduction>(res);
    },

    deleteLot: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/lots/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    verifierConformiteNF: async (lotId: string, payload: VerifierNFPayload): Promise<{ resultat: ConformiteNFResult; lot: LotProduction }> => {
      const res = await apiClient.post(`/operations/beton/lots/${lotId}/verifier-nf`, payload);
      return unwrap<{ resultat: ConformiteNFResult; lot: LotProduction }>(res);
    },

    // ── DNC (Sprint LB-C) ──────────────────────────────────
    listDNC: async (params: DNCListParams = {}): Promise<Paginated<DNC>> => {
      const res = await apiClient.get('/operations/beton/dnc', { params });
      return unwrapPaginated<DNC>(res);
    },

    getDNC: async (id: string): Promise<DNC> => {
      const res = await apiClient.get(`/operations/beton/dnc/${id}`);
      return unwrap<DNC>(res);
    },

    createDNC: async (payload: CreateDNCPayload): Promise<DNC> => {
      const res = await apiClient.post('/operations/beton/dnc', payload);
      return unwrap<DNC>(res);
    },

    updateDNC: async (id: string, payload: UpdateDNCPayload): Promise<DNC> => {
      const res = await apiClient.patch(`/operations/beton/dnc/${id}`, payload);
      return unwrap<DNC>(res);
    },

    deleteDNC: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/dnc/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Tarifs contrats cadre (LB-D) ───────────────────────
    listTarifs: async (params: TarifListParams = {}): Promise<Paginated<TarifContratCadre>> => {
      const res = await apiClient.get('/operations/beton/tarifs-contrat-cadre', { params });
      return unwrapPaginated<TarifContratCadre>(res);
    },

    getTarif: async (id: string): Promise<TarifContratCadre> => {
      const res = await apiClient.get(`/operations/beton/tarifs-contrat-cadre/${id}`);
      return unwrap<TarifContratCadre>(res);
    },

    createTarif: async (payload: CreateTarifPayload): Promise<TarifContratCadre> => {
      const res = await apiClient.post('/operations/beton/tarifs-contrat-cadre', payload);
      return unwrap<TarifContratCadre>(res);
    },

    updateTarif: async (id: string, payload: UpdateTarifPayload): Promise<TarifContratCadre> => {
      const res = await apiClient.patch(`/operations/beton/tarifs-contrat-cadre/${id}`, payload);
      return unwrap<TarifContratCadre>(res);
    },

    deleteTarif: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/tarifs-contrat-cadre/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    getPrixApplicable: async (clientId: string, produitId: string): Promise<PrixApplicable> => {
      const res = await apiClient.get('/operations/beton/tarifs-contrat-cadre/applicable', { params: { clientId, produitId } });
      return unwrap<PrixApplicable>(res);
    },

    // ── Alertes temps réel (Sprint LB-E) ───────────────────
    getAlertes: async (): Promise<AlertesResult> => {
      const res = await apiClient.get('/operations/beton/alertes');
      return unwrap<AlertesResult>(res);
    },

    // ════════ PRÉFABRIQUÉ Phase 1 (brief SG-LDB-CDC-ENRICHISSEMENT) ════════

    // ── Commandes clients ──────────────────────────────────────────────────
    listCommandes: async (params: CommandeListParams = {}): Promise<Paginated<Commande>> => {
      const res = await apiClient.get('/operations/beton/commandes', { params });
      return unwrapPaginated<Commande>(res);
    },
    getCommande: async (id: string): Promise<Commande> => {
      const res = await apiClient.get(`/operations/beton/commandes/${id}`);
      return unwrap<Commande>(res);
    },
    createCommande: async (payload: CreateCommandePayload): Promise<Commande> => {
      const res = await apiClient.post('/operations/beton/commandes', payload);
      return unwrap<Commande>(res);
    },
    updateCommande: async (id: string, payload: UpdateCommandePayload): Promise<Commande> => {
      const res = await apiClient.patch(`/operations/beton/commandes/${id}`, payload);
      return unwrap<Commande>(res);
    },
    deleteCommande: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/commandes/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Moules ─────────────────────────────────────────────────────────────
    listMoules: async (params: MouleListParams = {}): Promise<Paginated<Moule>> => {
      const res = await apiClient.get('/operations/beton/moules', { params });
      return unwrapPaginated<Moule>(res);
    },
    createMoule: async (payload: CreateMoulePayload): Promise<Moule> => {
      const res = await apiClient.post('/operations/beton/moules', payload);
      return unwrap<Moule>(res);
    },
    updateMoule: async (id: string, payload: UpdateMoulePayload): Promise<Moule> => {
      const res = await apiClient.patch(`/operations/beton/moules/${id}`, payload);
      return unwrap<Moule>(res);
    },
    deleteMoule: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/moules/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Formules béton ─────────────────────────────────────────────────────
    listFormules: async (): Promise<FormuleBeton[]> => {
      const res = await apiClient.get('/operations/beton/formules-beton');
      return unwrap<FormuleBeton[]>(res);
    },
    createFormule: async (payload: CreateFormuleBetonPayload): Promise<FormuleBeton> => {
      const res = await apiClient.post('/operations/beton/formules-beton', payload);
      return unwrap<FormuleBeton>(res);
    },
    updateFormule: async (id: string, payload: UpdateFormuleBetonPayload): Promise<FormuleBeton> => {
      const res = await apiClient.patch(`/operations/beton/formules-beton/${id}`, payload);
      return unwrap<FormuleBeton>(res);
    },
    deleteFormule: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/formules-beton/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Équipes production ─────────────────────────────────────────────────
    listEquipesProduction: async (): Promise<EquipeProduction[]> => {
      const res = await apiClient.get('/operations/beton/equipes-production');
      return unwrap<EquipeProduction[]>(res);
    },
    createEquipeProduction: async (payload: CreateEquipeProductionPayload): Promise<EquipeProduction> => {
      const res = await apiClient.post('/operations/beton/equipes-production', payload);
      return unwrap<EquipeProduction>(res);
    },
    updateEquipeProduction: async (id: string, payload: UpdateEquipeProductionPayload): Promise<EquipeProduction> => {
      const res = await apiClient.patch(`/operations/beton/equipes-production/${id}`, payload);
      return unwrap<EquipeProduction>(res);
    },
    deleteEquipeProduction: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/equipes-production/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ════════ SPRINT 2 — Modules 4 Gâchées · 5 QC Éprouvettes · 6 Parc ════════

    // ── Gâchées ───────────────────────────────────────────────────────────
    listGachees: async (params: GacheeListParams = {}): Promise<Paginated<Gachee>> => {
      const res = await apiClient.get('/operations/beton/gachees', { params });
      return unwrapPaginated<Gachee>(res);
    },
    getGachee: async (id: string): Promise<Gachee> => {
      const res = await apiClient.get(`/operations/beton/gachees/${id}`);
      return unwrap<Gachee>(res);
    },
    createGachee: async (payload: CreateGacheePayload): Promise<Gachee> => {
      const res = await apiClient.post('/operations/beton/gachees', payload);
      return unwrap<Gachee>(res);
    },
    updateGachee: async (id: string, payload: UpdateGacheePayload): Promise<Gachee> => {
      const res = await apiClient.patch(`/operations/beton/gachees/${id}`, payload);
      return unwrap<Gachee>(res);
    },
    validerGachee: async (id: string): Promise<Gachee> => {
      const res = await apiClient.post(`/operations/beton/gachees/${id}/valider`);
      return unwrap<Gachee>(res);
    },
    deleteGachee: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/gachees/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Éprouvettes / Contrôle qualité ────────────────────────────────────
    listEprouvettes: async (params: EprouvetteListParams = {}): Promise<Paginated<Eprouvette>> => {
      const res = await apiClient.get('/operations/beton/eprouvettes', { params });
      return unwrapPaginated<Eprouvette>(res);
    },
    getEprouvette: async (id: string): Promise<Eprouvette> => {
      const res = await apiClient.get(`/operations/beton/eprouvettes/${id}`);
      return unwrap<Eprouvette>(res);
    },
    createEprouvette: async (payload: CreateEprouvettePayload): Promise<Eprouvette> => {
      const res = await apiClient.post('/operations/beton/eprouvettes', payload);
      return unwrap<Eprouvette>(res);
    },
    testEprouvette: async (id: string, payload: TestEprouvettePayload): Promise<Eprouvette> => {
      const res = await apiClient.post(`/operations/beton/eprouvettes/${id}/test`, payload);
      return unwrap<Eprouvette>(res);
    },
    ecarterEprouvette: async (id: string, motif?: string): Promise<Eprouvette> => {
      const res = await apiClient.post(`/operations/beton/eprouvettes/${id}/ecarter`, { motif });
      return unwrap<Eprouvette>(res);
    },
    updateEprouvette: async (id: string, payload: UpdateEprouvettePayload): Promise<Eprouvette> => {
      const res = await apiClient.patch(`/operations/beton/eprouvettes/${id}`, payload);
      return unwrap<Eprouvette>(res);
    },
    deleteEprouvette: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/eprouvettes/${id}`);
      return unwrap<{ deleted: true }>(res);
    },
    getDashboardQC: async (): Promise<DashboardQC> => {
      const res = await apiClient.get('/operations/beton/eprouvettes/dashboard-qc');
      return unwrap<DashboardQC>(res);
    },

    // ── Parc préfabriqué ──────────────────────────────────────────────────
    listParc: async (params: ParcListParams = {}): Promise<Paginated<LotProduction>> => {
      const res = await apiClient.get('/operations/beton/parc', { params });
      return unwrapPaginated<LotProduction>(res);
    },
    getInventaireParc: async (): Promise<InventaireParc> => {
      const res = await apiClient.get('/operations/beton/parc/inventaire');
      return unwrap<InventaireParc>(res);
    },
    listMouvementsParc: async (params: MouvementParcListParams = {}): Promise<Paginated<MouvementParc>> => {
      const res = await apiClient.get('/operations/beton/parc/mouvements', { params });
      return unwrapPaginated<MouvementParc>(res);
    },
    placerLotParc: async (lotId: string, payload: PlacerParcPayload): Promise<LotProduction> => {
      const res = await apiClient.post(`/operations/beton/lots/${lotId}/parc/placer`, payload);
      return unwrap<LotProduction>(res);
    },
    transfererLotParc: async (lotId: string, payload: TransfertParcPayload): Promise<LotProduction> => {
      const res = await apiClient.post(`/operations/beton/lots/${lotId}/parc/transferer`, payload);
      return unwrap<LotProduction>(res);
    },
    reserverLotParc: async (lotId: string, payload: ReserverLotPayload): Promise<LotProduction> => {
      const res = await apiClient.post(`/operations/beton/lots/${lotId}/parc/reserver`, payload);
      return unwrap<LotProduction>(res);
    },
    libererLotParc: async (lotId: string): Promise<LotProduction> => {
      const res = await apiClient.post(`/operations/beton/lots/${lotId}/parc/liberer`);
      return unwrap<LotProduction>(res);
    },
    sortirLotParc: async (lotId: string, payload: SortirLotPayload = {}): Promise<LotProduction> => {
      const res = await apiClient.post(`/operations/beton/lots/${lotId}/parc/sortir`, payload);
      return unwrap<LotProduction>(res);
    },

    // ════════ SPRINT 3 — Modules 7 Stocks audit · 8 Expéditions BL ════════

    listMouvementsStock: async (params: MouvementStockListParams = {}): Promise<Paginated<MouvementStock>> => {
      const res = await apiClient.get('/operations/beton/stocks/mouvements', { params });
      return unwrapPaginated<MouvementStock>(res);
    },
    createMouvementStock: async (payload: CreateMouvementStockPayload): Promise<MouvementStock> => {
      const res = await apiClient.post('/operations/beton/stocks/mouvements', payload);
      return unwrap<MouvementStock>(res);
    },
    updateMatiereTypage: async (id: string, payload: UpdateMatiereTypagePayload): Promise<MatierePremiere> => {
      const res = await apiClient.patch(`/operations/beton/matieres-premieres/${id}/typage`, payload);
      return unwrap<MatierePremiere>(res);
    },
    getStockHealth: async (): Promise<StockHealth> => {
      const res = await apiClient.get('/operations/beton/stocks/health');
      return unwrap<StockHealth>(res);
    },

    listExpeditions: async (params: ExpeditionListParams = {}): Promise<Paginated<Expedition>> => {
      const res = await apiClient.get('/operations/beton/expeditions', { params });
      return unwrapPaginated<Expedition>(res);
    },
    getExpedition: async (id: string): Promise<Expedition> => {
      const res = await apiClient.get(`/operations/beton/expeditions/${id}`);
      return unwrap<Expedition>(res);
    },
    createExpedition: async (payload: CreateExpeditionPayload): Promise<Expedition> => {
      const res = await apiClient.post('/operations/beton/expeditions', payload);
      return unwrap<Expedition>(res);
    },
    updateExpedition: async (id: string, payload: UpdateExpeditionPayload): Promise<Expedition> => {
      const res = await apiClient.patch(`/operations/beton/expeditions/${id}`, payload);
      return unwrap<Expedition>(res);
    },
    preparerExpedition: async (id: string): Promise<Expedition> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${id}/preparer`);
      return unwrap<Expedition>(res);
    },
    expedierExpedition: async (id: string): Promise<Expedition> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${id}/expedier`);
      return unwrap<Expedition>(res);
    },
    livrerExpedition: async (id: string, payload: SignerLivraisonPayload): Promise<Expedition> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${id}/livrer`, payload);
      return unwrap<Expedition>(res);
    },
    refuserExpedition: async (id: string, payload: RefuserExpeditionPayload): Promise<Expedition> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${id}/refuser`, payload);
      return unwrap<Expedition>(res);
    },
    // BL-TERRAIN — visa du DAF et adresses temporaires (10 min) des signatures
    viserExpedition: async (id: string, note?: string): Promise<Expedition> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${id}/viser`, { note });
      return unwrap<Expedition>(res);
    },
    signaturesExpedition: async (id: string): Promise<{ client: string | null; chauffeur: string | null; photoBon: string | null }> => {
      const res = await apiClient.get(`/operations/beton/expeditions/${id}/signatures`);
      return unwrap<{ client: string | null; chauffeur: string | null; photoBon: string | null }>(res);
    },
    // BL-LIEN — PDF signé (le serveur le fabrique s'il manque et revérifie son empreinte) et révocation du lien du client
    pdfExpedition: async (id: string): Promise<Blob> => {
      const res = await apiClient.get(`/operations/beton/expeditions/${id}/pdf`, { responseType: 'blob' });
      return res.data as Blob;
    },
    revoquerLienClient: async (id: string): Promise<{ revoque: number }> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${id}/lien-client/revoquer`);
      return unwrap<{ revoque: number }>(res);
    },
    deleteExpedition: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/expeditions/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ════════ SPRINT 4 — Modules 9 Réclamations · 10 Facture auto ═══════

    genererFactureDepuisBL: async (expeditionId: string, payload: GenererFacturePayload = {}): Promise<Invoice> => {
      const res = await apiClient.post(`/operations/beton/expeditions/${expeditionId}/generer-facture`, payload);
      return unwrap<Invoice>(res);
    },
    creerAvoir: async (factureId: string, payload: CreerAvoirPayload): Promise<Invoice> => {
      const res = await apiClient.post(`/operations/beton/factures/${factureId}/avoir`, payload);
      return unwrap<Invoice>(res);
    },

    listReclamations: async (params: ReclamationListParams = {}): Promise<Paginated<ReclamationChantier>> => {
      const res = await apiClient.get('/operations/beton/reclamations', { params });
      return unwrapPaginated<ReclamationChantier>(res);
    },
    getReclamation: async (id: string): Promise<ReclamationChantier> => {
      const res = await apiClient.get(`/operations/beton/reclamations/${id}`);
      return unwrap<ReclamationChantier>(res);
    },
    createReclamation: async (payload: CreateReclamationPayload): Promise<ReclamationChantier> => {
      const res = await apiClient.post('/operations/beton/reclamations', payload);
      return unwrap<ReclamationChantier>(res);
    },
    updateReclamation: async (id: string, payload: UpdateReclamationPayload): Promise<ReclamationChantier> => {
      const res = await apiClient.patch(`/operations/beton/reclamations/${id}`, payload);
      return unwrap<ReclamationChantier>(res);
    },
    deleteReclamation: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/reclamations/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ════════ SPRINT 5 — Modules 11 Achats · 12 Maintenance moules ════════

    // Fournisseurs
    listFournisseurs: async (params: FournisseurListParams = {}): Promise<Paginated<Fournisseur>> => {
      const res = await apiClient.get('/operations/beton/fournisseurs', { params });
      return unwrapPaginated<Fournisseur>(res);
    },
    getFournisseur: async (id: string): Promise<Fournisseur> => {
      const res = await apiClient.get(`/operations/beton/fournisseurs/${id}`);
      return unwrap<Fournisseur>(res);
    },
    createFournisseur: async (payload: CreateFournisseurPayload): Promise<Fournisseur> => {
      const res = await apiClient.post('/operations/beton/fournisseurs', payload);
      return unwrap<Fournisseur>(res);
    },
    updateFournisseur: async (id: string, payload: UpdateFournisseurPayload): Promise<Fournisseur> => {
      const res = await apiClient.patch(`/operations/beton/fournisseurs/${id}`, payload);
      return unwrap<Fournisseur>(res);
    },
    deleteFournisseur: async (id: string): Promise<{ deleted: true } | Fournisseur> => {
      const res = await apiClient.delete(`/operations/beton/fournisseurs/${id}`);
      return unwrap<{ deleted: true } | Fournisseur>(res);
    },

    // BCA
    listBca: async (params: BcaListParams = {}): Promise<Paginated<BonCommandeAchat>> => {
      const res = await apiClient.get('/operations/beton/bca', { params });
      return unwrapPaginated<BonCommandeAchat>(res);
    },
    getBca: async (id: string): Promise<BonCommandeAchat> => {
      const res = await apiClient.get(`/operations/beton/bca/${id}`);
      return unwrap<BonCommandeAchat>(res);
    },
    createBca: async (payload: CreateBcaPayload): Promise<BonCommandeAchat> => {
      const res = await apiClient.post('/operations/beton/bca', payload);
      return unwrap<BonCommandeAchat>(res);
    },
    updateBca: async (id: string, payload: UpdateBcaPayload): Promise<BonCommandeAchat> => {
      const res = await apiClient.patch(`/operations/beton/bca/${id}`, payload);
      return unwrap<BonCommandeAchat>(res);
    },
    emettreBca: async (id: string): Promise<BonCommandeAchat> => {
      const res = await apiClient.post(`/operations/beton/bca/${id}/emettre`);
      return unwrap<BonCommandeAchat>(res);
    },
    annulerBca: async (id: string): Promise<BonCommandeAchat> => {
      const res = await apiClient.post(`/operations/beton/bca/${id}/annuler`);
      return unwrap<BonCommandeAchat>(res);
    },
    deleteBca: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/bca/${id}`);
      return unwrap<{ deleted: true }>(res);
    },
    createReception: async (bcaId: string, payload: CreateReceptionPayload): Promise<ReceptionAchat> => {
      const res = await apiClient.post(`/operations/beton/bca/${bcaId}/receptions`, payload);
      return unwrap<ReceptionAchat>(res);
    },
    listReceptions: async (params: { bonCommandeId?: string; page?: number; limit?: number } = {}): Promise<Paginated<ReceptionAchat>> => {
      const res = await apiClient.get('/operations/beton/receptions', { params });
      return unwrapPaginated<ReceptionAchat>(res);
    },

    // Maintenance moules
    listMaintenances: async (params: MaintenanceListParams = {}): Promise<Paginated<MaintenanceMoule>> => {
      const res = await apiClient.get('/operations/beton/maintenances-moule', { params });
      return unwrapPaginated<MaintenanceMoule>(res);
    },
    getMaintenanceAlertes: async (): Promise<{ total: number; alertes: Array<{ id: string; code: string; libelle: string; cycles: number; seuil: number; depassement: number }> }> => {
      const res = await apiClient.get('/operations/beton/maintenances-moule/alertes');
      return unwrap(res);
    },
    getMaintenance: async (id: string): Promise<MaintenanceMoule> => {
      const res = await apiClient.get(`/operations/beton/maintenances-moule/${id}`);
      return unwrap<MaintenanceMoule>(res);
    },
    createMaintenance: async (payload: CreateMaintenancePayload): Promise<MaintenanceMoule> => {
      const res = await apiClient.post('/operations/beton/maintenances-moule', payload);
      return unwrap<MaintenanceMoule>(res);
    },
    updateMaintenance: async (id: string, payload: UpdateMaintenancePayload): Promise<MaintenanceMoule> => {
      const res = await apiClient.patch(`/operations/beton/maintenances-moule/${id}`, payload);
      return unwrap<MaintenanceMoule>(res);
    },
    demarrerMaintenance: async (id: string): Promise<MaintenanceMoule> => {
      const res = await apiClient.post(`/operations/beton/maintenances-moule/${id}/demarrer`);
      return unwrap<MaintenanceMoule>(res);
    },
    terminerMaintenance: async (id: string, payload: { resultat?: string; coutCents?: number } = {}): Promise<MaintenanceMoule> => {
      const res = await apiClient.post(`/operations/beton/maintenances-moule/${id}/terminer`, payload);
      return unwrap<MaintenanceMoule>(res);
    },
    deleteMaintenance: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/maintenances-moule/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ════════ SPRINT 6 — Modules 13 AffectationOf · 15 Dashboard DG ════════

    getDashboardDg: async (): Promise<DashboardDg> => {
      const res = await apiClient.get('/operations/beton/dashboard-dg');
      return unwrap<DashboardDg>(res);
    },

    listAffectationsOf: async (params: AffectationOfListParams = {}): Promise<Paginated<AffectationOf>> => {
      const res = await apiClient.get('/operations/beton/affectations-of', { params });
      return unwrapPaginated<AffectationOf>(res);
    },
    getAffectationsByOrdre: async (ordreId: string): Promise<{ ordre: any; affectations: AffectationOf[]; synthese: any }> => {
      const res = await apiClient.get(`/operations/beton/ordres/${ordreId}/affectations`);
      return unwrap(res);
    },
    createAffectationOf: async (payload: CreateAffectationOfPayload): Promise<AffectationOf> => {
      const res = await apiClient.post('/operations/beton/affectations-of', payload);
      return unwrap<AffectationOf>(res);
    },
    updateAffectationOf: async (id: string, payload: UpdateAffectationOfPayload): Promise<AffectationOf> => {
      const res = await apiClient.patch(`/operations/beton/affectations-of/${id}`, payload);
      return unwrap<AffectationOf>(res);
    },
    deleteAffectationOf: async (id: string): Promise<{ deleted: true }> => {
      const res = await apiClient.delete(`/operations/beton/affectations-of/${id}`);
      return unwrap<{ deleted: true }>(res);
    },

    // ── Production journalière ──
    getProductionJournaliere: async (date?: string): Promise<ProductionJournaliereResponse> => {
      const res = await apiClient.get('/operations/beton/production-journaliere', { params: date ? { date } : {} });
      return unwrap<ProductionJournaliereResponse>(res);
    },
    declarerProductionJournaliere: async (payload: DeclarerProductionPayload): Promise<DeclarerProductionResponse> => {
      const res = await apiClient.post('/operations/beton/production-journaliere', payload);
      return unwrap<DeclarerProductionResponse>(res);
    },
    validerQcEtPlacer: async (payload: ValiderQcParcPayload): Promise<{ valides: number; zoneParc: string; lots: string[]; message: string }> => {
      const res = await apiClient.post('/operations/beton/production-journaliere/qc/valider', payload);
      return unwrap<{ valides: number; zoneParc: string; lots: string[]; message: string }>(res);
    },
    rejeterQcLots: async (payload: RejeterQcPayload): Promise<{ rejetes: number; lots: string[]; message: string }> => {
      const res = await apiClient.post('/operations/beton/production-journaliere/qc/rejeter', payload);
      return unwrap<{ rejetes: number; lots: string[]; message: string }>(res);
    },
  },
};

// ── Sprint 6 payloads ────────────────────────────────────────────────────

export interface AffectationOfListParams {
  ordreId?:  string;
  userId?:   string;
  role?:     RoleAffectationOf;
  dateFrom?: string;
  dateTo?:   string;
  page?:     number;
  limit?:    number;
}
export interface CreateAffectationOfPayload {
  ordreId:           string;
  userId:            string;
  role:              RoleAffectationOf;
  dateAffectation?:  string;
  heuresEffectuees?: number;
  notes?:            string;
}
export interface UpdateAffectationOfPayload {
  role?:             RoleAffectationOf;
  dateAffectation?:  string;
  heuresEffectuees?: number;
  notes?:            string;
}

// ── Sprint 5 — payloads ──────────────────────────────────────────────────

export interface FournisseurListParams {
  categorie?: CategorieFournisseur;
  actif?:     boolean;
  search?:    string;
  page?:      number;
  limit?:     number;
}
export interface CreateFournisseurPayload {
  code?:               string;
  raisonSociale:       string;
  categorie:           CategorieFournisseur;
  contactNom?:         string;
  email?:              string;
  tel?:                string;
  adresse?:            string;
  ville?:              string;
  pays?:               string;
  nif?:                string;
  iban?:               string;
  banque?:             string;
  delaiPaiementJours?: number;
  notes?:              string;
  actif?:              boolean;
}
export type UpdateFournisseurPayload = Partial<Omit<CreateFournisseurPayload, 'code'>>;

export interface BcaListParams {
  statut?:        StatutBonCommande;
  fournisseurId?: string;
  dateFrom?:      string;
  dateTo?:        string;
  search?:        string;
  page?:          number;
  limit?:         number;
}
export interface LigneBcaPayload {
  matierePremiereId:  string;
  quantite:           number;
  prixUnitaireCents:  number;
  notes?:             string;
}
export interface CreateBcaPayload {
  fournisseurId:        string;
  dateCommande:         string;
  dateLivraisonPrevue?: string;
  tvaPct?:              number;
  lignes:               LigneBcaPayload[];
  declencheurType?:     string;
  reference?:           string;
  notes?:               string;
}
export interface UpdateBcaPayload {
  dateLivraisonPrevue?: string;
  tvaPct?:              number;
  notes?:               string;
  statut?:              StatutBonCommande;
  lignes?:              LigneBcaPayload[];
}
export interface LigneReceptionPayload {
  ligneBcaId:         string;
  quantiteRecue:      number;
  prixUnitaireCents?: number;
  notes?:             string;
}
export interface CreateReceptionPayload {
  reference?:         string;
  dateReception?:     string;
  refBLFournisseur?:  string;
  lignes:             LigneReceptionPayload[];
  notes?:             string;
}

export interface MaintenanceListParams {
  statut?:        StatutMaintenanceMoule;
  mouleId?:       string;
  joursEcheance?: number;
  page?:          number;
  limit?:         number;
}
export interface CreateMaintenancePayload {
  mouleId:        string;
  type:           TypeMaintenanceMoule;
  datePlanifiee:  string;
  description:    string;
  technicienNom?: string;
  coutCents?:     number;
  reference?:     string;
}
export interface UpdateMaintenancePayload {
  type?:          TypeMaintenanceMoule;
  statut?:        StatutMaintenanceMoule;
  datePlanifiee?: string;
  description?:   string;
  technicienNom?: string;
  coutCents?:     number;
  resultat?:      string;
}

// ── Sprint 4 — payloads ────────────────────────────────────────────────

export interface GenererFacturePayload {
  tvaPct?:    number;
  dueInDays?: number;
}
export interface LigneAvoirPayload {
  designation:       string;
  quantite:          number;
  prixUnitaireCents: number;
  produitId?:        string;
}
export interface CreerAvoirPayload {
  motif:   string;
  lignes:  LigneAvoirPayload[];
  tvaPct?: number;
}

export interface ReclamationListParams {
  statut?:     StatutReclamation;
  chantierId?: string;
  search?:     string;
  page?:       number;
  limit?:      number;
}
export interface CreateReclamationPayload {
  chantierId:  string;
  objet:       string;
  description?: string;
  gravite?:    string;
  affecteId?:  string;
  reference?:  string;
  notes?:      string;
}
export interface UpdateReclamationPayload {
  objet?:       string;
  description?: string;
  gravite?:     string;
  affecteId?:   string;
  statut?:      StatutReclamation;
  resolution?:  string;
  notes?:       string;
}

// ── Sprint 3 — payloads & params ─────────────────────────────────────────

export interface MouvementStockListParams {
  type?:                TypeMouvementStock;
  matierePremiereId?:   string;
  gacheeId?:            string;
  ordreFabricationId?:  string;
  dateFrom?:            string;
  dateTo?:              string;
  page?:                number;
  limit?:               number;
}
export interface CreateMouvementStockPayload {
  matierePremiereId:  string;
  type:               TypeMouvementStock;
  quantite:           number;
  prixUnitaireCents?: number;
  refExterne?:        string;
  motif?:             string;
}
export interface UpdateMatiereTypagePayload {
  type?:           TypeMatierePremiere;
  diametreMm?:     number;
  classeAcier?:    string;
  classeCiment?:   string;
  granulometrie?:  string;
}

export interface ExpeditionListParams {
  statut?:     StatutExpedition;
  commandeId?: string;
  chantierId?: string;
  dateFrom?:   string;
  dateTo?:     string;
  search?:     string;
  /** BL-TERRAIN : origine du BL, ou seulement ceux émis au terrain et pas encore visés. */
  source?:     'bureau' | 'terrain';
  aViser?:     boolean;
  page?:       number;
  limit?:      number;
}
export interface LigneExpeditionPayload {
  lotId:    string;
  quantite: number;
  notes?:   string;
}
export interface CreateExpeditionPayload {
  commandeId?:       string;
  chantierId?:       string;
  adresseLivraison?: string;
  typeTransport?:    TypeTransportExpedition;
  immatriculations?: string;
  chauffeur?:        string;
  telChauffeur?:     string;
  sousTraitant?:     string;
  capaciteCamionKg?: number;
  lignes:            LigneExpeditionPayload[];
  reference?:        string;
  notes?:            string;
}
export interface UpdateExpeditionPayload extends Partial<Omit<CreateExpeditionPayload, 'lignes'>> {
  statut?: StatutExpedition;
  lignes?: LigneExpeditionPayload[];
}
export interface SignerLivraisonPayload {
  signataireNom:   string;
  signatureHash?:  string;
  notes?:          string;
}
export interface RefuserExpeditionPayload {
  raisonRefus: string;
}

// ── Sprint 2 — Payloads & params ─────────────────────────────────────────

export interface GacheeListParams {
  statut?:   StatutGachee;
  ordreId?:  string;
  dateFrom?: string;
  dateTo?:   string;
  page?:     number;
  limit?:    number;
}
export interface CreateGacheePayload {
  ordreId:         string;
  formuleBetonId?: string;
  operateurUserId?: string;
  centrale?:       string;
  heureCoulage:    string;
  volumeM3:        number;
  slumpMm?:        number;
  slumpCibleMm?:   number;
  tempEauC?:       number;
  tempAmbianteC?:  number;
  rapportEC?:      number;
  reference?:      string;
  notes?:          string;
}
export interface UpdateGacheePayload extends Partial<Omit<CreateGacheePayload, 'ordreId'>> {
  statut?: StatutGachee;
}

export interface EprouvetteListParams {
  statut?:        StatutEprouvette;
  gacheeId?:      string;
  joursEcheance?: number;
  page?:          number;
  limit?:         number;
}
export interface CreateEprouvettePayload {
  gacheeId:        string;
  typeEprouvette?: string;
  dateMoulage?:    string;
  fc28CibleMpa?:   number;
  reference?:      string;
  notes?:          string;
}
export interface TestEprouvettePayload {
  fcMesure:  number;
  dateTest?: string;
  notes?:    string;
}
export interface UpdateEprouvettePayload {
  typeEprouvette?: string;
  fc28CibleMpa?:   number;
  statut?:         StatutEprouvette;
  notes?:          string;
}

export interface ParcListParams {
  zone?:                string;
  commandeReserveeId?:  string;
  inclureSorties?:      boolean;
  page?:                number;
  limit?:               number;
}
export interface MouvementParcListParams {
  lotId?: string;
  type?:  TypeMouvementParc;
  page?:  number;
  limit?: number;
}
export interface PlacerParcPayload {
  zoneParc:        string;
  emplacementParc?: string;
  motif?:           string;
}
export interface TransfertParcPayload {
  zoneTo:          string;
  emplacementParc?: string;
  motif?:           string;
}
export interface ReserverLotPayload {
  commandeId: string;
  motif?:     string;
}
export interface SortirLotPayload {
  motif?: string;
}

export interface CommandeListParams {
  statut?:    StatutCommande;
  clientId?:  string;
  chantierId?: string;
  dateFrom?:  string;
  dateTo?:    string;
  search?:    string;
  page?:      number;
  limit?:     number;
}

export interface LigneCommandePayload {
  produitId:        string;
  quantite:         number;
  prixUnitaireFcfa: number;
  remiseLignePct?:  number;
  notes?:           string;
}

export interface CreateCommandePayload {
  clientId:               string;
  chantierId?:            string;
  dateCommande:           string;
  dateLivraisonSouhaitee?: string;
  remisePct?:             number;
  lignes:                 LigneCommandePayload[];
  notes?:                 string;
}

export interface UpdateCommandePayload extends Partial<Omit<CreateCommandePayload, 'lignes'>> {
  statut?:              StatutCommande;
  dateLivraisonReelle?: string;
  raisonAnnulation?:    string;
  lignes?:              LigneCommandePayload[];
}

export interface MouleListParams {
  statut?:  StatutMoule;
  search?:  string;
  page?:    number;
  limit?:   number;
}

export interface CreateMoulePayload {
  code:                     string;
  libelle:                  string;
  longueurMm?:              number;
  largeurMm?:               number;
  nbEmpreintes?:            number;
  dateMiseEnService?:       string;
  prochainEntretienCycles?: number;
  statut?:                  StatutMoule;
  notes?:                   string;
}
export type UpdateMoulePayload = Partial<CreateMoulePayload>;

export interface CreateFormuleBetonPayload {
  code:               string;
  libelle:            string;
  classeBeton:        ClasseBeton;
  cimentKgM3?:        number;
  sableKgM3?:         number;
  gravier5_15KgM3?:   number;
  gravier15_25KgM3?:  number;
  eauLitresM3?:       number;
  adjuvantKgM3?:      number;
  fc28TargetMpa?:     number;
  rapportEC?:         number;
  notes?:             string;
  actif?:             boolean;
}
export type UpdateFormuleBetonPayload = Partial<CreateFormuleBetonPayload>;

export interface CreateEquipeProductionPayload {
  code:        string;
  nom:         string;
  chefNom?:    string;
  specialite?: string;
  actif?:      boolean;
  notes?:      string;
}
export type UpdateEquipeProductionPayload = Partial<CreateEquipeProductionPayload>;

export interface ChantierListParams {
  statut?: StatutChantier;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateChantierPayload {
  code?:         string;
  nom:           string;
  clientId?:     string;
  adresse?:      string;
  volumeTotal:   number;
  volumeLivre?:  number;
  dateDebut:     string;
  dateFinPrevue?: string;
  statut?:       StatutChantier;
  notes?:        string;
  // Sprint 4 — Module 9
  maitreOuvrage?:          string;
  gpsLat?:                 number;
  gpsLng?:                 number;
  responsableNom?:         string;
  responsableTel?:         string;
  responsableEmail?:       string;
  planInstallationPdfUrl?: string;
}

export type UpdateChantierPayload = Partial<Omit<CreateChantierPayload, 'dateDebut'>> & {
  dateFinReelle?: string;
};

export interface OrdreListParams {
  statut?:    StatutOF;
  dateFrom?:  string;
  dateTo?:    string;
  produitId?: string;
  page?:      number;
  limit?:     number;
}

export interface CreateOrdrePayload {
  produitId:     string;
  quantite:      number;
  datePlanifiee: string;
  notes?:        string;
  reference?:    string;
  // ── Préfa Phase 1.3 ──
  commandeId?:          string;
  mouleId?:             string;
  equipeProductionId?:  string;
  dateCoulage?:         string;
}

export interface UpdateOrdrePayload {
  datePlanifiee?:       string;
  quantite?:            number;
  notes?:               string;
  commandeId?:          string | null;
  mouleId?:             string | null;
  equipeProductionId?:  string | null;
  dateCoulage?:         string | null;
}

export type OrdreTaskName = 'ferraillage' | 'coffrage' | 'coulage' | 'decoffrage';

export interface OrdreTaskPayload {
  at?: string;
}

export interface PlanningParams {
  days?:     number;
  dateFrom?: string;
}

export interface ToupieListParams {
  statut?: StatutToupie;
  actif?:  boolean;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateToupiePayload {
  code:                string;
  capaciteM3:          number;
  immatriculation?:    string;
  marque?:             string;
  statut?:             StatutToupie;
  dateMiseEnService?:  string;
  prochainEntretien?:  string;
  notes?:              string;
  actif?:              boolean;
}

export type UpdateToupiePayload = Partial<CreateToupiePayload>;

export interface LotListParams {
  statut?: StatutLot;
  page?:   number;
  limit?:  number;
}

export interface LotProductionWithOrdre extends LotProduction {
  ordre?: {
    id: string;
    reference: string;
    produit: {
      id: string; code: string; designation: string; famille: FamilleProduit;
      norme: string | null; classeProduit: string | null; uniteVente: string;
    };
  };
}

export interface CreateLotPayload {
  quantite:        number;
  dateProduction?: string;
  notes?:          string;
}

export interface UpdateLotPayload {
  statut?:            StatutLot;
  controleQualiteOk?: boolean;
  fc28Mesure?:        number;
  notes?:             string;
}

export interface VerifierNFPayload {
  fc28Mesure?:       number;
  longueurMesureeM?: number;
  chargeRuptureDaN?: number;
}

export interface DNCListParams {
  statut?: StatutDNC;
  lotId?:  string;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateDNCPayload {
  lotId?:           string;
  cause:            string;
  actionCorrective?: string;
  responsableId?:   string;
}

export interface UpdateDNCPayload {
  cause?:           string;
  actionCorrective?: string;
  statut?:          StatutDNC;
  responsableId?:   string;
  dateCloture?:     string;
}

export interface TarifListParams {
  clientId?:  string;
  produitId?: string;
  statut?:    StatutTarif;
  search?:    string;
  page?:      number;
  limit?:     number;
}

export interface CreateTarifPayload {
  clientId:    string;
  produitId:   string;
  contratRef:  string;
  prixHTCents: number;
  remisePct?:  number;
  dateDebut:   string;
  dateFin?:    string;
  statut?:     StatutTarif;
  notes?:      string;
}

export interface UpdateTarifPayload {
  contratRef?:  string;
  prixHTCents?: number;
  remisePct?:   number;
  dateFin?:     string;
  statut?:      StatutTarif;
  notes?:       string;
}

// ── Estimation production (LB-B v2.2) ────────────────────────────────────────

export interface CompositionBetonResult {
  classeBeton: 20 | 25 | 30 | 35;
  cimentKg:    number;
  sableKg:     number;
  gravierKg:   number;
  eauL:        number;
  adjuvantL:   number;
  totalKg:     number;
  rapportEC:   number;
  conforme:    boolean;
}

export interface CadenceDongyueResult {
  quantitePu:        number;
  rendementPct:      number;
  cadencePuParHeure: number;
  cadencePuParJour:  number;
  dureeHeures:       number;
  dureeJoursOuvres:  number;
}

export interface PlanningPoteauxResult {
  quantitePoteaux:     number;
  moulesDispo:         number;
  capaciteJournaliere: number;
  cyclesNecessaires:   number;
  dureeJoursOuvres:    number;
  dureeHeures:         number;
}

export interface EstimationProduction {
  produit:      { id: string; code: string; designation: string; famille: FamilleProduit; uniteVente: string };
  quantite:     number;
  composition?: CompositionBetonResult;
  cadence?:     CadenceDongyueResult;
  planning?:    PlanningPoteauxResult;
}
